import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertTriangle, ArrowRightLeft, Printer, FileSignature, StickyNote, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { patientDol } from "@/lib/clinical";
import VitalsTab from "@/components/patient/VitalsTab";
import MedicationsTab from "@/components/patient/MedicationsTab";
import NotesTab from "@/components/patient/NotesTab";
import InvestigationsTab from "@/components/patient/InvestigationsTab";
import FluidTab from "@/components/patient/FluidTab";
import VentilatorTab from "@/components/patient/VentilatorTab";
import RespiratoryTab from "@/components/patient/RespiratoryTab";
import SummaryTab from "@/components/patient/SummaryTab";
import DischargeTab from "@/components/patient/DischargeTab";
import TimelineTab from "@/components/patient/TimelineTab";
import GrowthTab from "@/components/patient/GrowthTab";
import BillingTab from "@/components/patient/BillingTab";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function PatientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [tab, setTab] = useState("summary");
  const [showTransfer, setShowTransfer] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const openDocPdf = async (path) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after a short delay to allow the new tab to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open PDF"); }
  };

  const load = useCallback(
    () => api.get(`/patients/${id}`).then(({ data }) => setPatient(data)).catch((e) => console.warn("Patient load failed", e)),
    [id]
  );
  useEffect(() => { load(); }, [load]);

  if (!patient) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const isNurse = user?.role === "nurse";
  const isDoctor = user?.role === "doctor" || user?.role === "admin";

  const toggleCritical = async () => {
    try {
      const { data } = await api.patch(`/patients/${patient.id}`, { critical: !patient.critical });
      setPatient(data);
      toast.success(!patient.critical ? "Marked critical" : "Removed critical flag");
    } catch { toast.error("Failed"); }
  };
  const togglePhoto = async () => {
    try {
      const { data } = await api.patch(`/patients/${patient.id}`, { phototherapy: !patient.phototherapy });
      setPatient(data);
    } catch (e) { console.warn("Toggle phototherapy failed", e); toast.error("Failed"); }
  };

  const dob = patient.dob || patient.admission_date;
  const w = patient.current_weight_g || patient.birth_weight_g;

  return (
    <div className="space-y-6" data-testid="patient-detail">
      {/* Sticky header */}
      <div className="sticky top-[57px] z-10 -mx-4 lg:-mx-8 px-4 lg:px-8 py-4 bg-background/85 backdrop-blur border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/patients" className="text-muted-foreground hover:text-foreground"><ArrowLeft size={18} /></Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl font-semibold truncate">{patient.name}</h1>
                {patient.critical && <Badge variant="destructive" className="rounded-full">CRITICAL</Badge>}
                {patient.status === "discharged" && <Badge className="rounded-full">Discharged</Badge>}
              </div>
              <div className="text-xs text-muted-foreground font-mono">{patient.hospital_id} · Bed {patient.bed_number || "—"} · {patient.nicu_area || "—"}</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Meta label="Consultant" value={patient.consultant || "—"} />
            <Meta label="Weight" value={w ? `${w} g` : "—"} />
            <Meta label="GA" value={patient.gestational_age_weeks ? `${patient.gestational_age_weeks} w` : "—"} />
            <Meta label="DOL" value={patientDol(patient) ?? "—"} />
            <Button size="sm" variant={patient.critical ? "destructive" : "outline"} className="rounded-full" onClick={toggleCritical} data-testid="btn-toggle-critical">
              <AlertTriangle size={14} className="mr-1" /> {patient.critical ? "Critical" : "Mark critical"}
            </Button>
            <Button size="sm" variant={patient.phototherapy ? "default" : "outline"} className="rounded-full" onClick={togglePhoto}>
              {patient.phototherapy ? "Phototherapy on" : "Phototherapy"}
            </Button>
            {patient.status === "admitted" && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setShowTransfer(true)} data-testid="btn-transfer-bed">
                <ArrowRightLeft size={14} className="mr-1" /> Transfer bed
              </Button>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-full" data-testid="btn-print-docs">
                  <Printer size={14} className="mr-1" /> Print docs
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="end">
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-muted text-left"
                  onClick={() => openDocPdf(`/patients/${patient.id}/identification-ticket.pdf`)}
                  data-testid="btn-doc-ticket"
                >
                  <StickyNote size={14} />
                  <div>
                    <div className="font-medium">ID ticket</div>
                    <div className="text-[10px] text-muted-foreground">Small sticker for the file cover</div>
                  </div>
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-muted text-left"
                  onClick={() => openDocPdf(`/patients/${patient.id}/acknowledgement-slip.pdf`)}
                  data-testid="btn-doc-ack"
                >
                  <FileSignature size={14} />
                  <div>
                    <div className="font-medium">Acknowledgement slip</div>
                    <div className="text-[10px] text-muted-foreground">Charges + parent signature</div>
                  </div>
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-muted text-left text-destructive"
                  onClick={() => setShowConsent(true)}
                  data-testid="btn-doc-consent"
                >
                  <ShieldAlert size={14} />
                  <div>
                    <div className="font-medium">High-risk consent (हिन्दी)</div>
                    <div className="text-[10px] text-muted-foreground">Editable diagnosis + support</div>
                  </div>
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {patient.diagnosis && <div className="mt-2 text-sm"><span className="text-muted-foreground">Diagnosis:</span> {patient.diagnosis}</div>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="w-full flex flex-wrap justify-start bg-transparent gap-1 p-0 border-b border-border rounded-none h-auto">
          {[
            ["summary", "Summary"],
            ["vitals", "Vitals"],
            ["meds", "Medications"],
            ...(isNurse ? [] : [["notes", "Doctor notes"]]),
            ["inv", "Investigations"],
            ["fluid", "Fluid"],
            ["vent", "Respiratory"],
            ["growth", "Growth & Lines"],
            ["timeline", "Timeline"],
            ["billing", "Billing"],
            ["discharge", "Discharge"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <div className="pt-6">
          <TabsContent value="summary"><SummaryTab patient={patient} onJump={setTab} onPatientChange={setPatient} /></TabsContent>
          <TabsContent value="vitals"><VitalsTab patient={patient} /></TabsContent>
          <TabsContent value="meds"><MedicationsTab patient={patient} /></TabsContent>
          {!isNurse && <TabsContent value="notes"><NotesTab patient={patient} /></TabsContent>}
          <TabsContent value="inv"><InvestigationsTab patient={patient} /></TabsContent>
          <TabsContent value="fluid"><FluidTab patient={patient} /></TabsContent>
          <TabsContent value="vent"><RespiratoryTab patient={patient} /></TabsContent>
          <TabsContent value="growth"><GrowthTab patient={patient} /></TabsContent>
          <TabsContent value="timeline"><TimelineTab patient={patient} /></TabsContent>
          <TabsContent value="billing"><BillingTab patient={patient} /></TabsContent>
          <TabsContent value="discharge"><DischargeTab patient={patient} onDischarged={load} /></TabsContent>
        </div>
      </Tabs>
      {showTransfer && (
        <TransferBedModal
          patient={patient}
          onClose={() => setShowTransfer(false)}
          onDone={(p) => { setPatient(p); setShowTransfer(false); toast.success(`Moved to bed ${p.bed_number}`); }}
        />
      )}
      {showConsent && (
        <HighRiskConsentModal
          patient={patient}
          onClose={() => setShowConsent(false)}
          onPrint={(query) => { openDocPdf(`/patients/${patient.id}/high-risk-consent.pdf${query}`); setShowConsent(false); }}
        />
      )}
    </div>
  );
}

function HighRiskConsentModal({ patient, onClose, onPrint }) {
  const [diagnosis, setDiagnosis] = useState(patient.diagnosis || "");
  // Prefill from patient.respiratory_support; user can add/remove.
  const [supports, setSupports] = useState(() => {
    const s = new Set();
    const rs = (patient.respiratory_support || "").toLowerCase();
    if (rs === "ventilator" || rs === "cpap" || rs === "hfnc" || rs === "oxygen") s.add(rs);
    return s;
  });
  const toggle = (k) => setSupports((prev) => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const print = () => {
    const params = new URLSearchParams();
    if (diagnosis.trim() && diagnosis !== patient.diagnosis) params.set("diagnosis", diagnosis.trim());
    else if (diagnosis.trim()) params.set("diagnosis", diagnosis.trim());
    if (supports.size) params.set("supports", Array.from(supports).join(","));
    onPrint(params.toString() ? `?${params.toString()}` : "");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="high-risk-consent-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2">
            <ShieldAlert size={16}/> High-risk consent (Hindi)
          </DialogTitle>
          <DialogDescription>
            Auto-fills the diagnosis and respiratory support onto the printed Hindi consent form. Edit if needed before printing — the patient record is not modified.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Diagnosis (as briefed to parents)</Label>
            <Textarea rows={2} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)}
                      placeholder="e.g. Preterm 30 weeks with RDS + suspected sepsis"
                      data-testid="consent-diagnosis" />
          </div>
          <div>
            <Label>Respiratory support being provided</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                ["oxygen", "ऑक्सीजन (Oxygen)"],
                ["cpap", "CPAP"],
                ["hfnc", "HHFNC"],
                ["ventilator", "वेंटिलेटर (Ventilator)"],
              ].map(([k, l]) => (
                <button key={k} type="button" onClick={() => toggle(k)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          supports.has(k)
                            ? "bg-destructive/10 border-destructive/40 text-destructive"
                            : "border-border text-muted-foreground hover:bg-muted"
                        }`}
                        data-testid={`consent-support-${k}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={print} data-testid="consent-print">Print consent</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferBedModal({ patient, onClose, onDone }) {
  const [bed, setBed] = useState("");
  const [area, setArea] = useState(patient.nicu_area || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!bed.trim()) return toast.error("Enter the new bed number");
    setSaving(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/transfer`, {
        new_bed_number: bed.trim(),
        new_nicu_area: area || null,
        reason: reason || null,
      });
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="transfer-bed-modal">
        <DialogHeader>
          <DialogTitle>Transfer bed — {patient.name}</DialogTitle>
          <DialogDescription>Current bed: <b>{patient.bed_number || "—"}</b> ({patient.nicu_area || "—"}). The new bed must be free.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>New bed *</Label>
            <Input value={bed} onChange={(e) => setBed(e.target.value)} placeholder="e.g. L2-05" autoFocus data-testid="tb-bed" />
          </div>
          <div>
            <Label>New NICU area</Label>
            <Input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Level II / III / Isolation" data-testid="tb-area" />
          </div>
          <div className="col-span-2">
            <Label>Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. step-down to L2 after weaning off vent" data-testid="tb-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tb-save">{saving ? "Moving…" : "Transfer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value }) {
  return (
    <div className="hidden md:flex flex-col px-3 py-1 rounded-lg bg-muted/60">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular">{value}</span>
    </div>
  );
}
